import { apiSend, SLOW_AI_REQUEST_TIMEOUT_MS } from '@/api/client'
import { acknowledgeAlert, closePhase } from '@/api/mutations'
import { queryKeys, siteAnalysesQuery, siteQuery } from '@/api/queries'
import type { SitePayload } from '@/api/schemas'
import { AnalysisDrawer } from '@/components/drawers/AnalysisDrawer'
import { EventDrawer } from '@/components/drawers/EventDrawer'
import { SiteDashboard } from '@/components/site/SiteDashboard'
import { ErrorCard, LoadingCard } from '@/components/ui/StateCards'
import { localDateInTz } from '@/lib/format'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Outlet, createFileRoute, useMatches, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { t } from '@/lib/i18n'

const drawerSearchSchema = z.object({
  event: z.coerce.number().int().positive().optional(),
  analysisType: z.enum(['batch', 'hourly', 'daily', 'weekly']).optional(),
  analysisId: z.coerce.number().int().positive().optional(),
  // WEB-021: YYYY-MM-DD in the site's local timezone. Falsy/malformed values
  // are dropped here so a stale URL still mounts the dashboard on today.
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
})

export const Route = createFileRoute('/sites/$siteId')({
  validateSearch: drawerSearchSchema,
  component: SiteRoute,
})

function SiteRoute() {
  const { siteId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/sites/$siteId' })
  const matches = useMatches()
  // When a child route (cameras/*, alerts) is matched, don't render the
  // dashboard underneath — only render the child via <Outlet />. Otherwise
  // the dashboard would sit under the child page because TanStack Router
  // nests these routes by filename convention.
  const leafRouteId = matches[matches.length - 1]?.routeId
  const isChildRoute = leafRouteId !== '/sites/$siteId'

  const selectedDate = search.date ?? null
  const siteQ = useQuery(siteQuery(siteId, selectedDate))
  const analysesQ = useQuery(siteAnalysesQuery(siteId, selectedDate))
  const queryClient = useQueryClient()
  const [ackingAlerts, setAckingAlerts] = useState<Set<number>>(new Set())
  const [closingPhases, setClosingPhases] = useState<Set<number>>(new Set())

  // Today in the site's timezone — needed for the date picker upper bound
  // and to know whether to show the "Today" reset pill. Falls back to the
  // browser's local date until the site query resolves.
  const siteTz = siteQ.data?.site.timezone ?? null
  const today = localDateInTz(siteTz)
  const minDate = siteQ.data ? localDateInTz(siteTz, siteQ.data.site.created_at) : today
  const activeDate = selectedDate ?? today

  const changeDate = (nextYmd: string) => {
    navigate({
      resetScroll: false,
      search: (prev) => ({
        ...prev,
        date: nextYmd === today ? undefined : nextYmd,
      }),
    })
  }

  const closeDrawers = () =>
    navigate({
      resetScroll: false,
      search: (prev) => ({
        ...prev,
        event: undefined,
        analysisType: undefined,
        analysisId: undefined,
      }),
    })

  // Optimistic-update key must match the exact cache entry of the currently
  // viewed date (WEB-021). Invalidation uses the site prefix and fans out to
  // every cached date automatically.
  const siteDateKey = queryKeys.siteDate(siteId, selectedDate)
  const ackMutation = useMutation({
    mutationFn: (alertId: number) => acknowledgeAlert(siteId, alertId),
    onMutate: async (alertId) => {
      setAckingAlerts((prev) => new Set(prev).add(alertId))
      await queryClient.cancelQueries({ queryKey: queryKeys.site(siteId) })
      const previous = queryClient.getQueryData<SitePayload>(siteDateKey)
      if (previous) {
        queryClient.setQueryData<SitePayload>(siteDateKey, {
          ...previous,
          alerts_pending: previous.alerts_pending.filter((a) => a.id !== alertId),
          stats: {
            ...previous.stats,
            alerts_pending: Math.max(0, previous.stats.alerts_pending - 1),
          },
        })
      }
      return { previous }
    },
    onError: (_err, _alertId, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(siteDateKey, ctx.previous)
    },
    onSettled: (_data, _err, alertId) => {
      setAckingAlerts((prev) => {
        const next = new Set(prev)
        next.delete(alertId)
        return next
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.siteAlerts(siteId) })
    },
  })

  const closePhaseMutation = useMutation({
    mutationFn: (phaseId: number) => closePhase(siteId, phaseId),
    onMutate: (phaseId) => {
      setClosingPhases((prev) => new Set(prev).add(phaseId))
    },
    onSettled: (_data, _err, phaseId) => {
      setClosingPhases((prev) => {
        const next = new Set(prev)
        next.delete(phaseId)
        return next
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) })
    },
  })

  // WP-polish restore: the Project Documents panel was taken off the main
  // dashboard in WP2A3B for the main view's declutter pass, but no
  // replacement location was ever wired up -- leaving no way to upload a
  // project document (e.g. a site's construction schedule) at all. The
  // backend endpoints and the DocumentsPanel component were left untouched
  // for exactly this, so this restores the three mutations that feed it.
  const uploadDocMutation = useMutation({
    mutationFn: async (file: File) => {
      // The server requires every mutating request to echo the cc_csrf
      // cookie back as an X-CSRF-Token header (main.py's CSRF
      // double-submit check) -- this was missing here, which is why every
      // upload/delete/generate-context call was silently rejected with
      // 403 "missing or invalid csrf token" before the DocumentsPanel
      // upload-clarity fix made the failure visible at all. Can't route
      // this one through api/client.ts's apiSend() because it always sets
      // Content-Type: application/json on a body, which would corrupt a
      // multipart file upload -- so the same cookie read is inlined here,
      // matching the pattern api/mutations.ts's streamChat() already uses
      // for its own non-JSON request.
      const csrfMatch =
        typeof document !== 'undefined'
          ? document.cookie.match(/(?:^|;\s*)cc_csrf=([^;]+)/)
          : null
      const csrf = csrfMatch && csrfMatch[1] ? decodeURIComponent(csrfMatch[1]) : null
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch(`/api/sites/${encodeURIComponent(siteId)}/documents`, {
        method: 'POST',
        headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
        body: fd,
      })
      if (!r.ok) {
        let detail = r.statusText
        try {
          const body = await r.json()
          if (body && typeof body.detail === 'string') detail = body.detail
        } catch {
          // Body wasn't JSON; keep statusText.
        }
        throw new Error(t('Upload failed: {detail}', { detail }))
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) }),
  })

  const deleteDocMutation = useMutation({
    mutationFn: (docId: number) =>
      apiSend(`/api/sites/${encodeURIComponent(siteId)}/documents/${docId}`, { method: 'DELETE' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) }),
  })

  const generateContextMutation = useMutation({
    mutationFn: () =>
      apiSend(`/api/sites/${encodeURIComponent(siteId)}/generate-context`, { method: 'POST' },
        { timeoutMs: SLOW_AI_REQUEST_TIMEOUT_MS }),
    // WP-SCHED: generate-context now only produces a DRAFT schedule
    // version (server/api_v2.py) -- it never touches the live site
    // payload's phases/brief by itself, so invalidating queryKeys.site
    // alone would leave the new draft invisible until an unrelated
    // refetch. ScheduleReviewPanel reads queryKeys.scheduleVersions.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.site(siteId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduleVersions(siteId) })
    },
  })

  // If a nested route (camera detail, alerts history) is matched, the
  // parent dashboard must step aside so the child can own the viewport.
  if (isChildRoute) return <Outlet />

  if (siteQ.isPending) return <LoadingCard label={t('Loading site…')} shape="site" />
  if (siteQ.isError) return <ErrorCard message={siteQ.error.message} onRetry={siteQ.refetch} />

  return (
    <>
      <SiteDashboard
        payload={siteQ.data}
        siteUpdateToday={analysesQ.data?.site_update_today ?? null}
        selectedDate={activeDate}
        today={today}
        minDate={minDate}
        onChangeDate={changeDate}
        onAcknowledgeAlert={(id) => ackMutation.mutate(id)}
        onClosePhase={(id) => closePhaseMutation.mutate(id)}
        onUploadDocument={(file) => uploadDocMutation.mutateAsync(file)}
        onDeleteDocument={(id) => deleteDocMutation.mutate(id)}
        onGenerateContext={() => generateContextMutation.mutateAsync()}
        ackingAlerts={ackingAlerts}
        closingPhases={closingPhases}
        documentsBusy={uploadDocMutation.isPending || deleteDocMutation.isPending}
        generatingContext={generateContextMutation.isPending}
      />
      {search.event !== undefined ? (
        <EventDrawer
          siteId={siteId}
          eventId={search.event}
          onClose={closeDrawers}
          onNavigate={(next) =>
            navigate({ resetScroll: false, search: (prev) => ({ ...prev, event: next }) })
          }
        />
      ) : null}
      {search.analysisType !== undefined && search.analysisId !== undefined ? (
        <AnalysisDrawer
          siteId={siteId}
          analysisType={search.analysisType}
          analysisId={search.analysisId}
          onClose={closeDrawers}
        />
      ) : null}
    </>
  )
}
